import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../lib/toast';
import { Plus, Trash2, Printer, Save, FileText } from 'lucide-react';

const MOTIVOS = [
  { id: 'merma', label: 'Merma' },
  { id: 'vencido', label: 'Vencido' },
  { id: 'robo', label: 'Robo' },
  { id: 'uso_interno', label: 'Uso interno' },
  { id: 'cortesia_regalo', label: 'Cortesía / Regalo' },
  { id: 'desperdicio_preparacion', label: 'Desperdicio en preparación' },
  { id: 'ajuste', label: 'Otro ajuste' }
];

const emptyRow = () => ({ key: Date.now() + Math.random(), text: '', item: null, qty: '', motivo: 'merma', nota: '', showSug: false });
const num = (v) => parseFloat(String(v).replace(',', '.')) || 0;
const fechaLarga = () => new Date().toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

export const ExitAdjustmentFormat = ({ warehouses, onRegistered }) => {
  const { api, user } = useAuth();
  const [areaId, setAreaId] = useState(warehouses[0]?.area_id || '');
  const [stock, setStock] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const area = warehouses.find(w => w.area_id === areaId);

  useEffect(() => {
    if (!areaId) return;
    api.get(`/inventory/areas/${areaId}/stock?t=${Date.now()}`).then(r => setStock(r.data || [])).catch(() => setStock([]));
  }, [areaId, api]);

  const update = (key, patch) => setRows(rs => rs.map(r => r.key === key ? { ...r, ...patch } : r));
  const removeRow = (key) => setRows(rs => rs.length > 1 ? rs.filter(r => r.key !== key) : [emptyRow()]);

  const suggestions = (text) => text.length < 1 ? [] : stock.filter(s => s.supply_name?.toLowerCase().includes(text.toLowerCase())).slice(0, 8);

  const validRows = rows.filter(r => r.item && num(r.qty) > 0);
  const totalCost = validRows.reduce((s, r) => s + num(r.qty) * (r.item.cost_per_unit || 0), 0);
  const motivoLabel = (id) => MOTIVOS.find(m => m.id === id)?.label || id;

  const register = async () => {
    if (validRows.length === 0) return toast.error('Agrega al menos un insumo con cantidad');
    const incomplete = rows.filter(r => (r.text || r.qty) && !(r.item && num(r.qty) > 0));
    if (incomplete.length > 0) return toast.error('Hay filas incompletas: elige el insumo de la lista y pon cantidad');
    setSaving(true);
    let ok = 0;
    for (const r of validRows) {
      try {
        await api.post('/stock-adjustments', {
          supply_id: r.item.supply_id,
          item_type: 'supply',
          item_name: r.item.supply_name,
          adjustment_type: r.motivo,
          quantity: -Math.abs(num(r.qty)),
          unit: r.item.unit || '',
          reason: r.nota?.trim() ? `${motivoLabel(r.motivo)} · ${r.nota.trim()}` : motivoLabel(r.motivo),
          area_id: areaId,
          cost_per_unit: r.item.cost_per_unit || 0
        });
        ok++;
      } catch (e) {
        toast.error(`${r.item.supply_name}: ${e.response?.data?.detail || 'error al registrar'}`);
      }
    }
    setSaving(false);
    if (ok > 0) {
      toast.success(`${ok} salida(s) registrada(s) en ${area?.name}`);
      setRows([emptyRow()]);
      api.get(`/inventory/areas/${areaId}/stock?t=${Date.now()}`).then(r => setStock(r.data || []));
      onRegistered?.();
    }
  };

  const printHtml = () => `
    <div style="font-family: Arial, sans-serif; padding: 24px; color:#111;">
      <div style="border-bottom:3px solid #111; padding-bottom:8px; display:flex; justify-content:space-between; align-items:flex-end;">
        <div>
          <div style="font-size:20px; font-weight:800; letter-spacing:1px;">FORMATO DE SALIDAS POR AJUSTE</div>
          <div style="font-size:12px; color:#555; text-transform:capitalize;">${fechaLarga()}</div>
        </div>
        <div style="font-size:12px; text-align:right;">
          <div><b>Almacén:</b> ${area?.name || ''}</div>
          <div style="margin-top:4px;"><b>Responsable:</b> ${user?.name || ''}</div>
        </div>
      </div>
      <table style="width:100%; border-collapse:collapse; margin-top:16px; font-size:13px;">
        <thead><tr style="background:#111; color:#fff;">
          <th style="padding:8px; text-align:left; width:30px;">#</th>
          <th style="padding:8px; text-align:left;">Insumo</th>
          <th style="padding:8px; text-align:right; width:90px;">Cantidad</th>
          <th style="padding:8px; text-align:left; width:60px;">Unidad</th>
          <th style="padding:8px; text-align:left; width:150px;">Motivo</th>
          <th style="padding:8px; text-align:left;">Nota</th>
          <th style="padding:8px; text-align:right; width:90px;">Costo</th>
        </tr></thead>
        <tbody>
          ${validRows.map((r, i) => `
            <tr style="border-bottom:1px solid #bbb; height:34px;">
              <td style="padding:8px; color:#666;">${i + 1}</td>
              <td style="padding:8px; font-weight:700;">${r.item.supply_name}</td>
              <td style="padding:8px; text-align:right; font-weight:700;">${num(r.qty).toLocaleString('es-MX')}</td>
              <td style="padding:8px;">${r.item.unit || ''}</td>
              <td style="padding:8px;">${motivoLabel(r.motivo)}</td>
              <td style="padding:8px; color:#444;">${r.nota || ''}</td>
              <td style="padding:8px; text-align:right;">$${(num(r.qty) * (r.item.cost_per_unit || 0)).toFixed(2)}</td>
            </tr>`).join('')}
          <tr><td colspan="6" style="padding:10px 8px; text-align:right; font-weight:700;">Costo total de la salida</td><td style="padding:10px 8px; text-align:right; font-weight:800; font-size:15px;">$${totalCost.toFixed(2)}</td></tr>
        </tbody>
      </table>
      <div style="margin-top:36px; display:flex; justify-content:space-between; font-size:12px;">
        <div>Entregó: ______________________</div>
        <div>Autorizó: ______________________</div>
      </div>
    </div>`;

  const print = () => {
    if (validRows.length === 0) return toast.error('Agrega al menos un insumo con cantidad para imprimir');
    openPrint(printHtml());
  };

  const blankHtml = () => `
    <div style="font-family: Arial, sans-serif; padding: 24px; color:#111;">
      <div style="border-bottom:3px solid #111; padding-bottom:8px; display:flex; justify-content:space-between; align-items:flex-end;">
        <div>
          <div style="font-size:20px; font-weight:800; letter-spacing:1px;">FORMATO DE SALIDAS POR AJUSTE</div>
          <div style="font-size:12px; color:#555;">Fecha: ____ / ____ / ________ &nbsp;&nbsp; Turno: ______________</div>
        </div>
        <div style="font-size:12px; text-align:right;">
          <div><b>Almacén:</b> ${area?.name || '______________________'}</div>
          <div style="margin-top:4px;"><b>Responsable:</b> ______________________</div>
        </div>
      </div>
      <table style="width:100%; border-collapse:collapse; margin-top:16px; font-size:13px;">
        <thead><tr style="background:#111; color:#fff;">
          <th style="padding:8px; text-align:left; width:30px;">#</th>
          <th style="padding:8px; text-align:left;">Insumo</th>
          <th style="padding:8px; text-align:left; width:90px;">Cantidad</th>
          <th style="padding:8px; text-align:left; width:70px;">Unidad</th>
          <th style="padding:8px; text-align:left; width:160px;">Motivo</th>
          <th style="padding:8px; text-align:left;">Nota / Observación</th>
          <th style="padding:8px; text-align:center; width:70px;">Capturado</th>
        </tr></thead>
        <tbody>
          ${Array.from({ length: 18 }).map((_, i) => `
            <tr style="border-bottom:1px solid #999; height:36px;">
              <td style="padding:6px 8px; color:#666; border-right:1px solid #ddd;">${i + 1}</td>
              <td style="border-right:1px solid #ddd;"></td>
              <td style="border-right:1px solid #ddd;"></td>
              <td style="border-right:1px solid #ddd;"></td>
              <td style="border-right:1px solid #ddd;"></td>
              <td style="border-right:1px solid #ddd;"></td>
              <td style="text-align:center;"><span style="display:inline-block; width:16px; height:16px; border:2px solid #111; border-radius:3px;"></span></td>
            </tr>`).join('')}
        </tbody>
      </table>
      <div style="margin-top:10px; font-size:11px; color:#555;">Motivos: Merma · Vencido · Robo · Uso interno · Cortesía / Regalo · Desperdicio en preparación · Otro</div>
      <div style="margin-top:30px; display:flex; justify-content:space-between; font-size:12px;">
        <div>Entregó: ______________________</div>
        <div>Autorizó: ______________________</div>
        <div>Capturó en sistema: ______________________</div>
      </div>
    </div>`;

  const openPrint = (html) => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Salidas por ajuste</title></head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-4 md:p-6 space-y-4" data-testid="exit-format">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-orange-500" /> Formato de Salidas por Ajuste</h2>
          <p className="text-sm text-gray-500 capitalize">{fechaLarga()} · Responsable: {user?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Almacén:</span>
          <select value={areaId} onChange={e => setAreaId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="exit-format-area-select">
            {warehouses.map(w => <option key={w.area_id} value={w.area_id}>{w.name}</option>)}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-900 text-white text-left">
              <th className="px-3 py-2 w-8">#</th>
              <th className="px-3 py-2">Insumo</th>
              <th className="px-3 py-2 w-28 text-right">Cantidad</th>
              <th className="px-3 py-2 w-20">Unidad</th>
              <th className="px-3 py-2 w-48">Motivo</th>
              <th className="px-3 py-2">Nota</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} className="border-b align-top" data-testid={`exit-row-${i}`}>
                <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                <td className="px-3 py-2 relative">
                  <Input
                    value={r.text}
                    placeholder="Escribe el insumo..."
                    onChange={e => update(r.key, { text: e.target.value, item: null, showSug: true })}
                    onFocus={() => update(r.key, { showSug: true })}
                    onBlur={() => setTimeout(() => update(r.key, { showSug: false }), 150)}
                    className={`h-9 ${r.text && !r.item ? 'border-amber-400' : ''}`}
                    data-testid={`exit-supply-input-${i}`}
                  />
                  {r.item && <p className="text-xs text-gray-500 mt-1">Stock actual: {Number(r.item.quantity || 0).toFixed(2)} {r.item.unit} · ${Number(r.item.cost_per_unit || 0).toFixed(2)}/{r.item.unit}</p>}
                  {r.showSug && !r.item && suggestions(r.text).length > 0 && (
                    <div className="absolute z-20 left-3 right-3 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-auto">
                      {suggestions(r.text).map(s => (
                        <button key={s.supply_id} type="button" onMouseDown={() => update(r.key, { item: s, text: s.supply_name, showSug: false })}
                          className="w-full text-left px-3 py-2 hover:bg-orange-50 flex justify-between" data-testid={`exit-sug-${s.supply_id}`}>
                          <span className="font-medium">{s.supply_name}</span>
                          <span className="text-xs text-gray-500">{Number(s.quantity || 0).toFixed(2)} {s.unit}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Input type="text" inputMode="decimal" value={r.qty} onChange={e => update(r.key, { qty: e.target.value })} placeholder="0" className="h-9 text-right" data-testid={`exit-qty-input-${i}`} />
                </td>
                <td className="px-3 py-2 text-gray-600 pt-4">{r.item?.unit || '—'}</td>
                <td className="px-3 py-2">
                  <select value={r.motivo} onChange={e => update(r.key, { motivo: e.target.value })} className="border rounded-lg px-2 h-9 text-sm w-full bg-white" data-testid={`exit-motivo-select-${i}`}>
                    {MOTIVOS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <Input value={r.nota} onChange={e => update(r.key, { nota: e.target.value })} placeholder="Opcional" className="h-9" data-testid={`exit-nota-input-${i}`} />
                </td>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => removeRow(r.key)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`exit-remove-row-${i}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button type="button" variant="outline" onClick={() => setRows(rs => [...rs, emptyRow()])} className="gap-1" data-testid="exit-add-row-btn">
        <Plus className="w-4 h-4" /> Agregar insumo
      </Button>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t pt-4">
        <p className="text-sm text-gray-700" data-testid="exit-format-summary">
          <b>{validRows.length}</b> insumo(s) · Costo total de la salida: <b className="text-red-600">${totalCost.toFixed(2)}</b>
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => openPrint(blankHtml())} className="gap-1" data-testid="exit-print-blank-btn"><Printer className="w-4 h-4" /> Imprimir formato en blanco</Button>
          <Button type="button" variant="outline" onClick={print} className="gap-1" data-testid="exit-print-btn"><Printer className="w-4 h-4" /> Imprimir con datos</Button>
          <Button type="button" onClick={register} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="exit-register-btn">
            <Save className="w-4 h-4" /> {saving ? 'Registrando...' : 'Registrar salidas'}
          </Button>
        </div>
      </div>
    </div>
  );
};
